import React, { useState, ReactElement } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress } from '@material-ui/core'
import clsx from 'clsx'
import Button from '@objects/button'
import { Colors } from '@config/theme/palette'

import { sendSubscription } from '@services/api/autobahnVotingPhase1'

import { useAutobahnplakateTexts } from '../store'
import FontSize from '@config/theme/definitions/fontSize'
import Copy from '@components/copy'

const useStyles = makeStyles((theme) => ({
  root: {},
  inputRoot: {
    position: 'relative',
  },
  input: {
    width: '100%',
    height: '58px',
    border: `1px solid ${Colors.grey[700]}`,
    borderRadius: theme.spacing(2),
    fontFamily: theme.typography.fontFamily2,
    fontSize: FontSize['base'],
    padding: theme.spacing(0, 16, 0, 4),

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 20, 0, 4),
    },

    '&::placeholder': {
      color: theme.palette.button.disabled,
    },
  },
  inputError: {
    borderColor: theme.palette.error.dark,
  },
  label: {
    display: 'block',
    fontFamily: theme.typography.fontFamily2,
    fontWeight: 500,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  buttonRoot: {
    position: 'absolute',
    top: '50%',
    right: theme.spacing(2),
    display: 'inline-block',
    transform: 'translateY(-50%)',

    [theme.breakpoints.up('lg')]: {
      right: theme.spacing(4),
    },
  },
  button: {
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.button.main,
    color: theme.palette.button.mainText,
    width: theme.spacing(10),
    height: theme.spacing(10),

    '&:not(.disabled):hover': {
      backgroundColor: theme.palette.button.tertiaryHover,
      color: theme.palette.button.tertiaryTextHover,
    },
  },
  buttonLoading: {
    pointerEvents: 'none',
    color: theme.palette.button.tertiaryTextHover,
  },
  loadingIndicator: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    color: theme.palette.button.mainText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  info: {
    fontSize: FontSize['sm'],
    fontWeight: 400,
    textAlign: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 0, 0, 0),

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(2, 10, 0, 10),
    },
  },

  error: {
    color: theme.palette.error.dark,
    fontWeight: 500,
    margin: theme.spacing(1, 0, 0, 0),
  },

  success: {
    fontWeight: 500,
    textAlign: 'center',
  },
}))

type EmailFormProps = {
  setDone: (done: boolean) => void
}

export default function EmailForm({ setDone }: EmailFormProps): ReactElement {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [mailAddress, setMailAddress] = useState('')
  const store = useAutobahnplakateTexts()
  const errorId = 'voting-email-error'

  function send() {
    setLoading(true)
    setError(false)

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(mailAddress)) {
      setLoading(false)
      setError(true)
      return
    }

    sendSubscription(mailAddress)
      .then((res) => {
        setLoading(false)
        if (res && res.status === 201) {
          setDone(true)
        }
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }

  function onChange(event: React.ChangeEvent<HTMLInputElement>) {
    setMailAddress(event.target.value)
    setError(false)
  }

  return (
    <div className={classes.root}>
      <label htmlFor="voting-email" className={classes.label}>
        {store.resultEMailLabel}
      </label>
      <div className={classes.inputRoot}>
        <input
          type="email"
          className={clsx(classes.input, {
            [classes.inputError]: error,
          })}
          id="voting-email"
          placeholder={store.resultEMailPlaceholder}
          value={mailAddress}
          onChange={onChange}
          aria-invalid={error}
          aria-describedby={error ? errorId : undefined}
          aria-errormessage={error ? errorId : undefined}
        />

        <div className={classes.buttonRoot}>
          <Button
            type="icon"
            onClick={send}
            icon="ArrowMehrAchtung"
            className={clsx(classes.button, {
              [classes.buttonLoading]: loading,
            })}
          />
          {loading && (
            <div className={classes.loadingIndicator}>
              <CircularProgress size={20} color="inherit" />
            </div>
          )}
        </div>
      </div>
      {error && (
        <p
          id={errorId}
          className={classes.error}
          role="alert"
          aria-live="assertive"
        >
          {store.resultEMailErrorMessage}
        </p>
      )}
      <Copy className={classes.info} richtext={store.resultEMailPrivacyInfo} />
    </div>
  )
}
